import { ValidatePassword, ValidatePasswordMessage } from "@swivl-mono/helpers";
import { AccountInterface } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ImageUploader, ImageUploaderType, Input } from "@swivl-mono/ui";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AccountModel from "../../../Models/Account/AccountModel";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import Logo from "../Assets/SwivlLogo.png";

interface AccountWithPassword extends Omit<AccountInterface, "id" | "username" | "createdAt" | "updatedAt"> {
  password: string;
  avatarURL: string;
}
export function SignUpView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { invites } = AccountModel.useState();
  const navigate = useNavigate();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [account, setAccount] = React.useState<AccountWithPassword>({
    firstName: "",
    lastName: "",
    email: "",
    avatarURL: "https://swivl-cdn.s3-us-west-1.amazonaws.com/studio/member-avatar-blank.png",
    password: "",
  });
  const inviteUUID = searchParams.get("invite");

  useEffect(() => {
    if (inviteUUID) {
      if (invites) {
        const invite = invites.find((i) => i.uuid === inviteUUID);

        if (invite) {
          setAccount({
            ...account,
            firstName: invite.firstName,
            lastName: invite.lastName,
            email: invite.email,
          });
        }
      } else {
        AccountModel.loadInvite(inviteUUID);
      }
    }
  }, [inviteUUID, invites]);

  const submit = async () => {
    if (!account.email) {
      setError("Email is required");
      return;
    }
    if (!account.firstName) {
      setError("First Name is required");
      return;
    }
    if (!account.lastName) {
      setError("Last Name is required");
      return;
    }
    if (!account.password) {
      setError("Password is required");
      return;
    }

    const isValidPassword = ValidatePassword(account.password);
    if (isValidPassword === false) {
      setError(ValidatePasswordMessage);
      return;
    }
    setSaving(true);

    AccountModel.signUp(
      account.firstName,
      account.lastName,
      account.email,
      account.avatarURL,
      account.password,
      inviteUUID as string | undefined,
      navigate,
    )
      // .then((account) => {})
      .catch((error) => {
        if (error.message) {
          if (error.message === "Username is taken") {
            navigate("/login");
            AlertsModel.errorToast("An account already exists with this email, please log in");
            return;
          }
          setError(error.message);
          if (error.message === "Email already exists") {
            setError("An account already exists with this email");
          }
        } else {
          setError("Account Creation Failed");
        }
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="SignUpView flex flex-col items-center">
      <img src={Logo} alt="Swivl Logo" className="w-1/2 mx-auto mb-8" />

      <h2 className="mb-4">Welcome</h2>
      <div className="container flex-row flex items-center justify-center mb-3">
        <ImageUploader
          isEditable={true}
          imageURL={account.avatarURL}
          type={ImageUploaderType.AVATAR}
          imageUploaded={(url) => {
            setAccount((prevAccount) => ({ ...prevAccount, avatarURL: url }));
          }}
          text="Upload Image"
        />
      </div>
      <div className="container mx-auto">
        <label className="text-grey-700" htmlFor="email">
          Email
        </label>
        <Input
          placeholder="Email"
          name="email"
          value={account.email}
          onSet={(value: string) => {
            setAccount({ ...account, email: value });
          }}
        />
      </div>
      <div className="container w-100 flex space-x-3">
        <div className="flex-1">
          <label className="text-grey-700" htmlFor="firstName">
            First name
          </label>

          <Input
            placeholder="First Name"
            name="firstName"
            value={account.firstName}
            onSet={(value: string) => {
              setAccount({ ...account, firstName: value });
            }}
          />
        </div>
        <div className="flex-1">
          <label className="text-grey-700" htmlFor="lastName">
            Last Name
          </label>

          <Input
            placeholder="Last Name"
            name="lastName"
            value={account.lastName}
            onSet={(value: string) => {
              setAccount({ ...account, lastName: value });
            }}
          />
        </div>
      </div>
      <div className="container mx-auto mb-7">
        <label className="text-grey-700" htmlFor="password">
          Password
        </label>
        <Input
          type={Input.Type.Password}
          name="password"
          placeholder="Password"
          value={account.password}
          onSet={(value: string) => {
            setAccount({ ...account, password: value });
          }}
        />
      </div>
      {error && <div className="text-red-500 text-center mb-4">{error}</div>}

      <Button
        loading={saving}
        onClick={submit}
        size={ButtonSize.LARGE}
        color={ButtonColor.ORANGE}
      >
        Submit
      </Button>
    </div>
  );
}
